//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useEffect, useContext, useState } from "react";
import { Context } from "../common/context/Context";
import { Helmet } from "react-helmet-async";
import { Card, Col, Container, Form, Row, Button } from "react-bootstrap";
import { ClientSpecificActions } from "./clientSpecificActions";
import AlertMessage from "../common/alertMessages/alertMessages";
import ErrorAlertMessage from "../common/alertMessages/errorAlertMessage";
import { ManageClientsActions } from "../clients/manageClients/manageClientsActions";

const AddClientSpecificContent = ({ cmsData }) => {
  const context = useContext(Context);

  const [clientSpecificCMSData, setClientSpecificCMSData] = useState({
    title: "",
    description: "",
    tabNavigationTitle: "",
    pageContent: {},
  });
  const [countryOptions, setCountryOptions] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [programTemplateOptions, setProgramTemplateOptions] = useState([]);
  const [country, setCountry] = useState("");
  const [persona, setPersona] = useState("");
  const [type, setType] = useState("");
  const [program, setProgram] = useState("");
  const [programTemplate, setProgramTemplate] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [invalidClient, setInvalidClient] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [resourceFields, setResourceFields] = useState([
    {
      resourceId: "",
    },
  ]);
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    showAlert: false,
  });
  const [errorAlert, setErrorAlert] = useState({
    showAlert: false,
  });
  const showErrorModal = () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    refErrorMessageAlert.showAlert = false;
    setErrorAlert(refErrorMessageAlert);
  };

  useEffect(() => {
    setClientSpecificCMSData(cmsData);
  }, [cmsData]);

  const fetchClientSpecificLabel = (key) => {
    if (
      clientSpecificCMSData?.pageContent &&
      clientSpecificCMSData?.pageContent["ClientSpecificContentComponent"]
    ) {
      const clientSpecificCMSDataLabel =
        clientSpecificCMSData.pageContent["ClientSpecificContentComponent"];
      return ManageClientsActions.returnLabel(
        clientSpecificCMSDataLabel?.Labels,
        key
      );
    }
    return "";
  };

  const fetchCommonComponentLabel = (key) => {
    if (
      clientSpecificCMSData?.pageContent &&
      clientSpecificCMSData?.pageContent["CommonComponent"]
    ) {
      const clientSpecificCMSDataLabel =
        clientSpecificCMSData.pageContent["CommonComponent"];
      return ManageClientsActions.returnLabel(
        clientSpecificCMSDataLabel?.Labels,
        key
      );
    }
    return "";
  };

  const clientContentSave = () => {
    let addOperation = "add";
    let payload = {};
    if (type) {
      payload = {
        CompanyId: companyId,
        ProgramTemplateId: programTemplate,
        ProgramClassId: program,
        RightTrackPersonaId: persona,
        CountryCode: country,
        Type: type,
        ResourceId: resourceFields?.map((item) => ({ Id: item?.resourceId })),
        Operation: addOperation,
      };
    } else {
      payload = {};
    }
    console.log("payload", payload);
    let refAlert = JSON.parse(JSON.stringify(alert));
    ClientSpecificActions.CreateClientContentSave(payload).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          if (res?.data !== "False") {
            refAlert.showAlert = true;
            refAlert.type = "success";
            refAlert.message = fetchClientSpecificLabel(
              "ClientSpecificContent.SaveSuccessMessage"
            );
            setCountry("");
            setPersona("");
            setType("");
            setProgram("");
            setProgram("");
            setProgramTemplate("");
            setClientId("");
            setClientName("");
            setCompanyId("");
            setResourceFields([
              {
                resourceId: "",
              },
            ]);
          } else {
            refAlert.showAlert = true;
            refAlert.type = "danger";
            refAlert.message = fetchClientSpecificLabel(
              "ClientSpecificContent.SaveErrorMessage"
            );
          }
        } else if (
          res.status == 400 &&
          res.data ==
          "Client specific program video already exists for this Client - Program class combination, please remove the video before proceeding."
        ) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = fetchClientSpecificLabel(
            "ClientSpecificContent.OnlyOneClientSpecificProgramVideoErrorMessage"
          );
        } else if (
          res.status === 500 ||
          res.status === 400 ||
          res.status === 401
        ) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = fetchClientSpecificLabel(
            "ClientSpecificContent.SaveErrorMessage"
          );
        } else if (res.status === 204) {
          refAlert.showAlert = true;
          refAlert.type = "danger";
          refAlert.message = res
            ? res
            : fetchClientSpecificLabel(
              "ClientSpecificContent.SaveNoResponseMessage"
            );
        }
        setAlert(refAlert);
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const getCascadeClientData = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    ClientSpecificActions.GetCascadeClientData().then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          context.setCascadeClientData(res.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const getRTPersona = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    ClientSpecificActions.GetRTPersona().then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          context.setRTPersona(res.data && res.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const fetchClientsData = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    await ClientSpecificActions.GetClientsList().then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          let resData = res.data && res.data;
          const options = [];
          options.push({
            value: fetchClientSpecificLabel("ClientSpecificContent.ClientId"),
            label: fetchClientSpecificLabel("ClientSpecificContent.ClientId"),
          });
          resData.forEach((item) => {
            options.push({
              value: item?.rightTrackCompanyId,
              label: item?.rightTrackCompanyId,
            });
          });
          setClientId(resData);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const getRTProgramClass = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    ClientSpecificActions.GetRTProgramClass().then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          context.setRTProgramClass(res.data && res.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const getRTProgramTemplate = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

    ClientSpecificActions.GetRTProgramTemplate().then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          context.setRTProgramTemplate(res.data && res.data);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const getCountriesList = async () => {
    let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
    let locale = "en-US";
    let key = "country";

    ClientSpecificActions.GetCountriesList(locale, key).then(
      (res) => {
        if (res.status === 200 || res.status === 204) {
          let countryLists = [];
          res?.data[0]?.items?.forEach((item) => {
            let country = {};
            country.title = item.title;
            item?.standardCodes?.forEach((subItem) => {
              if (subItem?.itemCodeStandardName === "rt") {
                country.standardCodeValue = subItem?.standardCodeValue;
                countryLists.push(country);
              }
            });
          });
          context.setCountryList(countryLists);
        } else {
          refErrorMessageAlert.showAlert = true;
          setErrorAlert(refErrorMessageAlert);
          window.scrollTo(0, 0);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const handleCountryChange = (e) => {
    let country = e.target.value;
    setCountry(country);

    setProgramTemplateOptions(
      context?.cascadeClientData
        ?.filter(
          (item) =>
            `${item?.rtPersonaId || ""}` === `${persona}` &&
            `${item?.programCountryCode || ""}` === `${country}`
        )
        ?.map((item) => ({
          rtProgramTemplateId: item?.programTemplateKey,
          rtProgramTemplateName: item?.programTemplate
            ?.trim()
            .replaceAll(/\s+/gimu, " "),
        }))
        ?.sort((a, b) =>
          a.rtProgramTemplateName.localeCompare(b.rtProgramTemplateName)
        )
        ?.filter(function (item, pos, ary) {
          return (
            !pos ||
            item.rtProgramTemplateName !== ary[pos - 1].rtProgramTemplateName
          );
        })
    );
    setProgramOptions([]);

    setProgramTemplate("");
    setProgram("");
  };
  const handlePersona = (e) => {
    let persona = e.target.value;
    setPersona(persona);
    setCountryOptions(
      context?.cascadeClientData
        ?.filter((item) => `${item?.rtPersonaId || ""}` === `${persona}`)
        ?.map((item) => ({
          standardCodeValue: item?.programCountryCode
            ?.trim()
            .replaceAll(/\s+/gimu, " "),
          title: item?.programCountry?.trim().replaceAll(/\s+/gimu, " "),
        }))
        ?.sort((a, b) => a.title.localeCompare(b.title))
        ?.filter(function (item, pos, ary) {
          return !pos || item.title !== ary[pos - 1].title;
        })
    );
    setProgramTemplateOptions([]);
    setProgramOptions([]);

    setCountry("");
    setProgramTemplate("");
    setProgram("");
  };
  const handleType = (e) => {
    let type = e.target.value;
    setType(type);
  };
  const handleProgram = (e) => {
    let program = e.target.value;
    setProgram(program);
  };
  const handleCompanyId = (e) => {
    let companyId = e.target.value;
    if (companyId) {
      if (!isNaN(companyId)) {
        setCompanyId(companyId);
        setInvalidClient(false);
        setClientName("");
      } else {
        setClientName("");
        setCompanyId("");
        //setInvalidClient(true);
      }
    } else {
      setClientName("");
      setCompanyId("");
      setInvalidClient(false);
    }
  };
  const handleProgramTemplate = (e) => {
    let programtemplate = e.target.value;
    setProgramTemplate(programtemplate);

    setProgramOptions(
      context?.cascadeClientData
        ?.filter(
          (item) =>
            `${item?.rtPersonaId || ""}` === `${persona}` &&
            `${item?.programCountryCode || ""}` === `${country}` &&
            `${item?.programTemplateKey || ""}` === `${programtemplate}`
        )
        ?.map((item) => ({
          programclassId: item?.programClassId,
          programClass: item?.programClass
            ?.trim()
            .replaceAll(/\s+/gimu, " "),
        }))
        ?.sort((a, b) => a.programClass.localeCompare(b.programClass))
        ?.filter(function (item, pos, ary) {
          return !pos || item.programClass !== ary[pos - 1].programClass;
        })
    );

    setProgram("");
  };
  useEffect(() => {
    getCountriesList();
    getRTPersona();
    getCascadeClientData();
    getRTProgramClass();
    getRTProgramTemplate();
    fetchClientsData();
  }, []);

  const addResourceField = () => {
    setResourceFields([
      ...resourceFields,
      {
        resourceId: "",
      },
    ]);
  };

  const removeResourceField = (index) => {
    const newFields = [...resourceFields];
    newFields.splice(index, 1);
    setResourceFields(newFields);
  };

  let handleResourceIdChange = (i, e) => {
    let newValues = [...resourceFields];
    newValues[i][e.target.name] = e.target.value;
    setResourceFields(newValues);
  };

  const showModal = () => {
    let refAlert = JSON.parse(JSON.stringify(alert));
    refAlert.showAlert = false;
    setAlert(refAlert);
  };

  const validateClientId = () => {
    let clientDetails = clientId.filter(
      (data) => data.rightTrackCompanyId == companyId
    );
    if (clientDetails[0]?.companyName) {
      setClientName(clientDetails[0]?.companyName);
    } else {
      setInvalidClient(true);
      setClientName("");
    }
  };
  return (
    <React.Fragment>
      <Helmet title="Advanced Inputs" />
      <Container fluid className="p-0 mt-4">
        <Card>
          {errorAlert?.showAlert && (
            <ErrorAlertMessage
              alertMessage="AllPages.ErrorAlertMessage"
              showAlert={showErrorModal}
            />
          )}
          <Card.Header>
            <Card.Title>
              {" "}
              {fetchClientSpecificLabel(
                "ClientSpecificContent.AddNewClientSpecific"
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col lg="6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    {" "}
                    {fetchClientSpecificLabel("ClientSpecificContent.Type")}
                  </Form.Label>
                  <Form.Select
                    name="type"
                    value={type}
                    onChange={handleType}
                    aria-label="Type Select"
                  >
                    <option value="">Type</option>
                    <option key="Article" value="Article">
                      Article
                    </option>
                    <option key="Document" value="Document">
                      Document
                    </option>
                    {/* <option key="Video" value="Video">
                      Video
                    </option> */}
                    <option key="Ondemand Video" value="Ondemand Video">
                      Ondemand Video
                    </option>
                    <option key="Program Video" value="Program Video">
                      Program Video
                    </option>
                    <option key="Tool" value="Tool">
                      Tool
                    </option>
                    <option key="Video" value="Video">
                      Video
                    </option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {" "}
                    {fetchClientSpecificLabel("ClientSpecificContent.Persona")}
                  </Form.Label>
                  <Form.Select
                    name="persona"
                    value={persona}
                    onChange={handlePersona}
                    aria-label="Persona Select"
                  >
                    <option value="">
                      {fetchClientSpecificLabel(
                        "ClientSpecificContent.Persona"
                      )}
                    </option>
                    {context?.rtPersona?.map((item) => (
                      <option key={item?.rtPersonaId} value={item?.rtPersonaId}>
                        {item?.rtPersonaName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {" "}
                    {fetchClientSpecificLabel("ClientSpecificContent.Country")}
                  </Form.Label>
                  <Form.Select
                    name="country"
                    value={country}
                    onChange={handleCountryChange}
                    aria-label="Country Select"
                    disabled={!persona}
                  >
                    <option value="">
                      {fetchClientSpecificLabel(
                        "ClientSpecificContent.Country"
                      )}
                    </option>
                    {countryOptions?.map((item) => (
                      <option
                        key={item?.standardCodeValue}
                        value={item?.standardCodeValue}
                      >
                        {item?.title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {" "}
                    {fetchClientSpecificLabel("ClientSpecificContent.ClientId")}
                  </Form.Label>
                  {/* <Form.Select
                    name="clientid"
                    value={companyId}
                    onChange={handleCompanyId}
                    aria-label="ClientId Select"
                  >
                    <option value="">
                      {fetchClientSpecificLabel(
                        "ClientSpecificContent.ClientId"
                      )}
                    </option>
                    {clientId &&
                      clientId?.map((item) => (
                        <option
                          key={item?.rightTrackCompanyId}
                          value={item?.rightTrackCompanyId}
                        >
                          {item?.rightTrackCompanyId}
                        </option>
                      ))}
                  </Form.Select> */}
                  <div className="d-flex col-8">
                    <Form.Control
                      type="text"
                      name="clientid"
                      value={companyId}
                      className="col-2"
                      onChange={handleCompanyId}
                      aria-label="ClientId Text"
                    />
                    <Button
                      variant="primary"
                      className={
                        invalidClient
                          ? "btn-square text-center mx-1 col-6 disabled"
                          : "btn-square text-center mx-1 col-6"
                      }
                      onClick={() => validateClientId()}
                    >
                      Validate Client
                    </Button>
                  </div>
                  {clientName && (
                    <p>
                      <b>Client Name:</b> {clientName}
                    </p>
                  )}
                  {invalidClient && (
                    <p>
                      <b className="text-danger">Invalid Client Id</b>
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col lg="6">
                <Form.Group className="mb-3">
                  <Form.Label>
                    {" "}
                    {fetchClientSpecificLabel(
                      "ClientSpecificContent.ProgramTemplate"
                    )}
                  </Form.Label>
                  <Form.Select
                    name="rtProgramTemplate"
                    value={programTemplate}
                    onChange={handleProgramTemplate}
                    aria-label="Program Template Select"
                    disabled={!persona || !country}
                  >
                    <option value="">
                      {fetchClientSpecificLabel(
                        "ClientSpecificContent.ProgramTemplate"
                      )}
                    </option>
                    {programTemplateOptions?.map((item) => (
                      <option
                        key={item?.rtProgramTemplateId}
                        value={item?.rtProgramTemplateId}
                      >
                        {item?.rtProgramTemplateName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {" "}
                    {fetchClientSpecificLabel(
                      "ClientSpecificContent.ProgramClass"
                    )}
                  </Form.Label>
                  <Form.Select
                    name="programClass"
                    value={program}
                    onChange={handleProgram}
                    aria-label="Program Class Select"
                    disabled={!persona || !country || !programTemplate}
                  >
                    <option value="">
                      {fetchClientSpecificLabel(
                        "ClientSpecificContent.ProgramClass"
                      )}
                    </option>
                    {programOptions?.map((item) => (
                      <option
                        key={item?.programclassId}
                        value={item?.programclassId}
                      >
                        {item?.programClass}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {" "}
                    {fetchClientSpecificLabel(
                      "ClientSpecificContent.ResourceId"
                    )}
                  </Form.Label>
                  {resourceFields.map((field, index) => (
                    <>
                      <div className="d-flex">
                        <Form.Control
                          type="text"
                          name="resourceId"
                          value={field.resourceId}
                          onChange={(e) => handleResourceIdChange(index, e)}
                          aria-label="ResourceId Text"
                        />
                        <Button
                          variant="primary"
                          className="btn-square text-end mx-1"
                          onClick={addResourceField}
                        >
                          +
                        </Button>
                        {resourceFields.length !== 1 ? (
                          <Button
                            variant="primary"
                            className="btn-square text-end mx-1"
                            onClick={() => removeResourceField(index)}
                          >
                            -
                          </Button>
                        ) : (
                          ""
                        )}
                      </div>
                      <br />
                    </>
                  ))}
                </Form.Group>
              </Col>
            </Row>
            {alert?.showAlert && (
              <AlertMessage
                alertMessage={alert?.message}
                alertType={alert?.type}
                showAlert={showModal}
              />
            )}
          </Card.Body>
          <div className="d-flex mb-3 mx-3">
            <Button
              variant="primary"
              className={
                clientName
                  ? "btn-pill text-end mx-2"
                  : "btn-pill text-end mx-2 disabled"
              }
              onClick={clientContentSave}
            >
              {fetchCommonComponentLabel("Common.Save")}
            </Button>
          </div>
        </Card>
      </Container>
    </React.Fragment>
  );
};

export default AddClientSpecificContent;
